import { ENEMY_CATALOG, enemyMovementClass, type EnemyInstance } from './enemies'
import {
  footprintCenter,
  towerTargetClass,
  type TowerDefinition,
  type TowerInstance
} from './towers'
import { canTowerTargetEnemy } from '../combat/targeting/canTowerTargetEnemy'
import { normalizedPathProgress } from './path'

export type TargetingMode = 'FIRST' | 'LAST' | 'STRONG' | 'WEAK'

export const TARGETING_MODES: TargetingMode[] = ['FIRST', 'LAST', 'STRONG', 'WEAK']

export function isTargetingMode(value: string): value is TargetingMode {
  return (TARGETING_MODES as string[]).includes(value)
}

export function nextTargetingMode(current: TargetingMode): TargetingMode {
  const index = TARGETING_MODES.indexOf(current)
  return TARGETING_MODES[(index + 1) % TARGETING_MODES.length]
}

export function distanceCells(
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const dx = ax - bx
  const dy = ay - by
  return Math.hypot(dx, dy)
}

function inRangeCandidates(
  tower: TowerInstance,
  definition: TowerDefinition,
  enemies: EnemyInstance[],
  enemyCatalog: Record<string, { movementClass?: import('../combat/types/primitives').MovementClass }> = ENEMY_CATALOG,
  combatLevel?: Pick<import('./towers').TowerLevelDefinition, 'range' | 'combatEnabled'>
): EnemyInstance[] {
  const level = combatLevel ?? definition.levels[tower.level - 1]
  if (!level || level.combatEnabled === false) return []
  const range = level.range
  const center = footprintCenter(tower.anchor, definition.footprint)
  const towerView = { targetClass: towerTargetClass(definition) }
  const out: EnemyInstance[] = []
  for (const enemy of enemies) {
    if (!enemy.alive) continue
    const enemyDef = enemyCatalog[enemy.definitionId]
    if (
      !canTowerTargetEnemy(towerView, {
        movementClass: enemyMovementClass(enemyDef)
      })
    ) {
      continue
    }
    const dist = distanceCells(center.x, center.y, enemy.position.x, enemy.position.y)
    if (dist > range) continue
    out.push(enemy)
  }
  return out
}

/**
 * Route completion used to order enemies that may be on different routes.
 *
 * Session-spawned enemies carry routeLength, so progress compares as 0..1 and a
 * short lane beats a long one at the same absolute distance. Instances without
 * routeLength (hand-built fixtures) fall back to raw distance, which is the same
 * ordering whenever every candidate shares one route.
 */
export function targetingProgress(enemy: EnemyInstance): number {
  const length = enemy.routeLength
  if (typeof length === 'number' && Number.isFinite(length) && length > 0) {
    return normalizedPathProgress(enemy.pathProgress, length)
  }
  return enemy.pathProgress
}

/**
 * Prefer greater route progress; tie-break by lower id (deterministic spawn order).
 */
function preferProgressThenId(a: EnemyInstance, b: EnemyInstance): EnemyInstance {
  const pa = targetingProgress(a)
  const pb = targetingProgress(b)
  if (pa > pb) return a
  if (pb > pa) return b
  return a.id < b.id ? a : b
}

/**
 * Acquire a living in-range target for the tower's targeting mode.
 *
 * FIRST: greatest route progress → lower id
 * LAST: lowest route progress → lower id
 * STRONG: greatest current HP → greatest route progress → lower id
 * WEAK: lowest current HP → greatest route progress → lower id
 */
export function selectTarget(
  tower: TowerInstance,
  definition: TowerDefinition,
  enemies: EnemyInstance[],
  combatLevel?: Pick<import('./towers').TowerLevelDefinition, 'range' | 'combatEnabled'>
): EnemyInstance | null {
  const candidates = inRangeCandidates(tower, definition, enemies, ENEMY_CATALOG, combatLevel)
  if (candidates.length === 0) return null
  return pickFromCandidates(tower.targetingMode ?? 'FIRST', candidates)
}

/**
 * Primary + up to `extraCount` additional in-range eligible enemies.
 * Extras prefer lowest id among remaining (deterministic multi-lock).
 */
export function selectMultiLockTargets(
  tower: TowerInstance,
  definition: TowerDefinition,
  enemies: EnemyInstance[],
  extraCount: number,
  combatLevel?: Pick<import('./towers').TowerLevelDefinition, 'range' | 'combatEnabled'>
): EnemyInstance[] {
  const candidates = inRangeCandidates(tower, definition, enemies, ENEMY_CATALOG, combatLevel)
  if (candidates.length === 0) return []
  const primary = pickFromCandidates(tower.targetingMode ?? 'FIRST', candidates)
  if (!primary) return []
  if (extraCount <= 0) return [primary]
  const extras = candidates
    .filter((e) => e.id !== primary.id)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .slice(0, extraCount)
  return [primary, ...extras]
}

function pickFromCandidates(
  mode: TargetingMode,
  candidates: EnemyInstance[]
): EnemyInstance | null {
  if (candidates.length === 0) return null
  let best = candidates[0]!
  let bestProgress = targetingProgress(best)

  for (let i = 1; i < candidates.length; i += 1) {
    const enemy = candidates[i]!
    const progress = targetingProgress(enemy)
    switch (mode) {
      case 'LAST': {
        if (progress < bestProgress) {
          best = enemy
          bestProgress = progress
        } else if (progress === bestProgress && enemy.id < best.id) {
          best = enemy
        }
        break
      }
      case 'STRONG': {
        if (enemy.health > best.health) {
          best = enemy
          bestProgress = progress
        } else if (enemy.health === best.health) {
          best = preferProgressThenId(enemy, best)
          bestProgress = targetingProgress(best)
        }
        break
      }
      case 'WEAK': {
        if (enemy.health < best.health) {
          best = enemy
          bestProgress = progress
        } else if (enemy.health === best.health) {
          best = preferProgressThenId(enemy, best)
          bestProgress = targetingProgress(best)
        }
        break
      }
      case 'FIRST':
      default: {
        if (progress > bestProgress) {
          best = enemy
          bestProgress = progress
        } else if (progress === bestProgress && enemy.id < best.id) {
          best = enemy
        }
        break
      }
    }
  }

  return best
}

/** @deprecated Prefer selectTarget — kept for existing FIRST call sites/tests. */
export function selectFirstTarget(
  tower: TowerInstance,
  definition: TowerDefinition,
  enemies: EnemyInstance[]
): EnemyInstance | null {
  return selectTarget({ ...tower, targetingMode: 'FIRST' }, definition, enemies)
}
